import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import AppContainer from '../../../../containers/AppContainer/AppContainer';
import axiosInstance from '../../../../services/axios/axios';
import * as ancillaryActions from '../../../../store/ancillary/actions';
import * as appsActions from '../../../../store/apps/actions';
import * as uarActions from '../../../../store/uar/actions';
import { WrapImsPacket } from '../../../../store/utility';
import { selectModifyQueue } from '../../../../store/uar/selectors';

/**
 * Builds the next annual review modify-flow path from the current queue state.
 * @param {object} params helper inputs
 * @param {string} params.locationSearch current location search string
 * @param {Array} params.modifyQueue queued annual review requests
 * @returns {{ nextActionFlowPath: string, shouldAdvanceQueue: boolean, shouldClearQueue: boolean }}
 */
const getNextAnnualReviewActionFlowPath = ({ locationSearch, modifyQueue }) => {
	const searchParams = new URLSearchParams(locationSearch || '');
	const currentTrackingNumber = String(searchParams.get('tn') || '');
	const currentStep = Number(searchParams.get('uarStep') || 0);
	const preservedEsoFiId = String(searchParams.get('esoFiId') || '');
	const safeModifyQueue = Array.isArray(modifyQueue) ? modifyQueue : [];
	const esoFiId =
		preservedEsoFiId ||
		safeModifyQueue
			.map(entry => Number(entry?.staffMemberId))
			.filter(id => Number.isFinite(id) && id > 0)
			.join(',');
	const processedParams = new URLSearchParams({ view: 'processed' });

	if (esoFiId) {
		processedParams.set('esoFiId', esoFiId);
	}

	if (safeModifyQueue.length === 0) {
		return {
			nextActionFlowPath: `/UserAccessReview/Review/ActionFlow?${processedParams.toString()}`,
			shouldAdvanceQueue: false,
			shouldClearQueue: true,
		};
	}

	const currentIndexFromStep =
		Number.isFinite(currentStep) && currentStep > 0 ? currentStep - 1 : -1;
	const currentIndexFromTrackingNumber = safeModifyQueue.findIndex(item => {
		return String(item?.trackingNumber || '') === currentTrackingNumber;
	});
	const resolvedCurrentIndex =
		currentIndexFromStep >= 0 && currentIndexFromStep < safeModifyQueue.length
			? currentIndexFromStep
			: currentIndexFromTrackingNumber >= 0
				? currentIndexFromTrackingNumber
				: 0;
	const nextIndex = resolvedCurrentIndex + 1;

	if (nextIndex < safeModifyQueue.length) {
		const nextItem = safeModifyQueue[nextIndex];
		const params = new URLSearchParams({
			tn: nextItem.trackingNumber,
			uarSource: 'annualReview',
			uarTargetName:
				nextItem.staffMemberName || String(nextItem.staffMemberId || ''),
			uarStep: String(nextIndex + 1),
			uarTotal: String(safeModifyQueue.length),
			esoFiId,
		});

		return {
			nextActionFlowPath: `/UserAccessReview/Review/ActionFlow?${params.toString()}`,
			shouldAdvanceQueue: true,
			shouldClearQueue: false,
		};
	}

	return {
		nextActionFlowPath: `/UserAccessReview/Review/ActionFlow?${processedParams.toString()}`,
		shouldAdvanceQueue: false,
		shouldClearQueue: true,
	};
};

/**
 * Dedicated container for the annual review action flow.
 * Handles submit completion, queue advancement, and queue cleanup outside of AppContainer.
 */
const AnnualReviewActionContainer = ({ children }) => {
	const dispatch = useDispatch();
	const history = useHistory();
	const location = useLocation();
	const allFormsSubmitted = useSelector(state => state.apps.allFormsSubmitted);
	const trackingNumber = useSelector(state => state.apps.trackingNumber);
	const modifyQueue = useSelector(selectModifyQueue);
	const [sHasHandledSubmit, setSHasHandledSubmit] = useState(false);

	useEffect(() => {
		if (!allFormsSubmitted) {
			setSHasHandledSubmit(false);
			return;
		}

		if (sHasHandledSubmit) {
			return;
		}

		const onSubmitCompleteHandler = async () => {
			setSHasHandledSubmit(true);

			try {
				const imsPacket = WrapImsPacket({
					Completed: allFormsSubmitted,
				});
				const response = await axiosInstance.patch(
					`/api/Applications/Requests/${trackingNumber}`,
					imsPacket,
				);

				if (
					response?.status === 200 &&
					response?.data?.responseCode === 200
				) {
					const {
						nextActionFlowPath,
						shouldAdvanceQueue,
						shouldClearQueue,
					} = getNextAnnualReviewActionFlowPath({
						locationSearch: location.search,
						modifyQueue,
					});

					if (shouldAdvanceQueue) {
						dispatch(uarActions.advanceQueue());
					}

					if (shouldClearQueue) {
						dispatch(uarActions.resetQueue());
						dispatch(uarActions.clearActionCart());
					}

					history.push(nextActionFlowPath);
					return;
				}

				throw new Error('Annual review completion returned an unexpected response.');
			} catch {
				dispatch(ancillaryActions.clearMessages());
				dispatch(
					appsActions.updateSubmitting({
						updatedValue: false,
					}),
				);
				dispatch(
					appsActions.updateLoading({
						updatedValue: false,
					}),
				);
				dispatch(
					ancillaryActions.addMessage({
						Key: 3,
						Value: 'Error Completing Request.  Please refresh the page and submit again.',
					}),
				);
			}
		};

		onSubmitCompleteHandler();
	}, [
		allFormsSubmitted,
		dispatch,
		history,
		location.search,
		modifyQueue,
		sHasHandledSubmit,
		trackingNumber,
	]);

	return (
		<AppContainer
			useNextAsPrimaryAction
			handleCompletionExternally
		>
			{children}
		</AppContainer>
	);
};

AnnualReviewActionContainer.propTypes = {
	children: PropTypes.node,
};

AnnualReviewActionContainer.defaultProps = {
	children: null,
};

export default AnnualReviewActionContainer;
